<template>
  <div class="apple-page">
    <header class="hero-section">
      <h1 class="hero-title">{{ t.title }}</h1>
      <p class="hero-subtitle">{{ t.desc }}</p>
    </header>

    <section class="map-section">
      <div ref="mapContainer" class="kakao-map"></div>
    </section>

    <section class="content-section">
      <!-- Editor's Introduction Card -->
      <div class="glass-card intro-card mb-80">
        <div class="card-body">
          <span class="badge">Medical Guide</span>
          <h2>안심할 수 있는 동탄의 야간 및 비상 의료 인프라</h2>
          <p>
            부모로서 가장 당황스러운 순간은 늦은 밤이나 공휴일에 아이가 갑자기 아플 때입니다. 
            동탄 신도시는 다행히 <strong>달빛어린이병원</strong>(보건복지부 지정 야간 진료 기관)과 연중무휴 약국들이 잘 갖춰져 있어 빠른 대처가 가능합니다.
          </p>
          <p>
            응급실은 대기 시간이 길고 비용 부담이 클 수 있으므로, 경증 환자의 경우 오후 11시까지 운영하는 소아과 전문의 진료 기관을 먼저 확인하는 것이 현명합니다. 
            아래 리스트를 미리 숙지하시거나 즐겨찾기 해두시면 비상 상황에서 큰 도움이 됩니다.
          </p>
          <div class="tips-grid">
            <div class="tip-item">
              <span class="icon">🏥</span>
              <p>달빛어린이병원은 늦은 시간까지 운영하지만, 접수 마감 시간이 진료 종료 시간보다 빠를 수 있으니 출발 전 전화 확인은 필수입니다.</p>
            </div>
            <div class="tip-item">
              <span class="icon">💊</span>
              <p>'처방전 없이 살 수 있는 상비약'과 '야간 약국'의 위치를 미리 파악해 두는 것이 좋습니다.</p>
            </div>
          </div>
        </div>
      </div>

      <div class="section-header">
        <h2 class="section-title">{{ t.hospitalTitle }}</h2>
      </div>
      <div class="bento-grid">
        <div v-for="item in hospitals" :key="item.name" class="apple-card clickable" @click="focusOnMap(item)">
          <div class="card-content">
            <span class="card-tag emergency">{{ currentLang === 'ko' ? '병원' : 'Hospital' }}</span>
            <h3>{{ currentLang === 'ko' ? item.name : item.name_en }}</h3>
            <p class="address">{{ item.address }}</p>
            <p class="time">⏰ {{ currentLang === 'ko' ? item.time : item.time_en }}</p>
            <p class="info">ℹ️ {{ currentLang === 'ko' ? item.info : item.info_en }}</p>
            <button class="naver-btn" @click.stop="searchOnNaver(currentLang === 'ko' ? item.name : item.name_en)">Naver Search</button>
          </div>
        </div>
      </div>

      <div class="section-header mt-80">
        <h2 class="section-title">{{ t.pharmacyTitle }}</h2>
      </div>
      <div class="bento-grid">
        <div v-for="item in pharmacies" :key="item.name" class="apple-card clickable" @click="focusOnMap(item)">
          <div class="card-content">
            <span class="card-tag pharmacy">{{ currentLang === 'ko' ? '약국' : 'Pharmacy' }}</span>
            <h3>{{ currentLang === 'ko' ? item.name : item.name_en }}</h3>
            <p class="address">{{ item.address }}</p>
            <p class="time">⏰ {{ currentLang === 'ko' ? item.time : item.time_en }}</p>
            <p class="info">ℹ️ {{ currentLang === 'ko' ? item.info : item.info_en }}</p>
            <button class="naver-btn" @click.stop="searchOnNaver(currentLang === 'ko' ? item.name : item.name_en)">Naver Search</button>
          </div>
        </div>
      </div>

      <!-- Bottom Ad Unit -->
      <AdComponent slotId="9173007135" />
    </section>
  </div>
</template>

<script>
/* global kakao */
import { ref, onMounted, computed, inject, watch, onUnmounted, nextTick } from 'vue';
import data from '../assets/data.json';

const TRANSLATIONS = {
  ko: {
    title: '야간/응급 의료 정보',
    desc: '동탄 내 달빛어린이병원 및 늦게까지 운영하는 약국 정보입니다.',
    hospitalTitle: '소아과/응급실',
    pharmacyTitle: '야간/휴일 약국'
  },
  en: {
    title: 'Emergency Medical Info',
    desc: 'Night-care pediatrics and late-night pharmacies in Dongtan.',
    hospitalTitle: 'Pediatrics/ER',
    pharmacyTitle: 'Night-care Pharmacies'
  }
};

export default {
  name: 'HealthView',
  setup() {
    const currentLang = inject('currentLang', ref('ko'));
    const map = ref(null);
    const mapContainer = ref(null);
    const markers = ref([]);
    const t = computed(() => TRANSLATIONS[currentLang.value] || TRANSLATIONS['ko']);

    const hospitals = ref(data.health.hospitals);
    const pharmacies = ref(data.health.pharmacies);

    const initMap = () => {
      if (typeof kakao === 'undefined') {
        setTimeout(initMap, 500);
        return;
      }
      kakao.maps.load(() => {
        const container = mapContainer.value;
        if(!container) return;
        const options = { center: new kakao.maps.LatLng(37.205, 127.080), level: 7 };
        map.value = new kakao.maps.Map(container, options);

        // ✅ CSS 렌더링 완료 후 relayout → 마커 렌더
        nextTick(() => {
          setTimeout(() => {
            map.value.relayout();

            // 내 위치 가져오기
            if (navigator.geolocation) {
              navigator.geolocation.getCurrentPosition((position) => {
                const lat = position.coords.latitude;
                const lng = position.coords.longitude;
                const locPosition = new kakao.maps.LatLng(lat, lng);

                // 내 위치 마커 표시
                const imageSize = new kakao.maps.Size(24, 24);
                const markerImage = new kakao.maps.MarkerImage('https://t1.daumcdn.net/localimg/localimages/07/mapapidoc/markerStar.png', imageSize);
                new kakao.maps.Marker({
                  map: map.value,
                  position: locPosition,
                  image: markerImage,
                  title: "내 위치"
                });

                map.value.setCenter(locPosition);
              });
            }

            renderMarkers();
          }, 300);
        });
      });
    };

    const renderMarkers = () => {
      if (!map.value) return;
      markers.value.forEach(m => {
        if (m.iw) m.iw.close();
        m.setMap(null);
      });
      markers.value = [];
      
      const bounds = new kakao.maps.LatLngBounds();
      const allItems = [...hospitals.value, ...pharmacies.value];
      
      allItems.forEach(item => {
        const position = new kakao.maps.LatLng(item.lat, item.lng);
        const marker = new kakao.maps.Marker({
          position: position,
          map: map.value
        });
        
        const name = currentLang.value === 'ko' ? item.name : item.name_en;
        const iw = new kakao.maps.InfoWindow({ 
          content: `<div style="padding:10px;font-size:14px;color:#333;font-weight:600;min-width:150px;">${name}</div>`,
          removable: true
        });
        
        kakao.maps.event.addListener(marker, 'click', () => {
          markers.value.forEach(m => m.iw.close());
          iw.open(map.value, marker);
        });
        
        marker.iw = iw;
        marker.itemName = item.name;
        markers.value.push(marker);
        bounds.extend(position);
      });

      if (allItems.length > 0) {
        map.value.setBounds(bounds);
      }
    };

    const focusOnMap = (item) => {
      if (!map.value) return;
      const position = new kakao.maps.LatLng(item.lat, item.lng);
      map.value.setCenter(position);
      map.value.setLevel(4);
      
      const marker = markers.value.find(m => m.itemName === item.name);
      if (marker) {
        markers.value.forEach(m => m.iw.close());
        marker.iw.open(map.value, marker);
      }
      
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    onMounted(async () => {
      await nextTick();
      setTimeout(initMap, 600);
    });

    onUnmounted(() => {
      // Clean up if needed
    });

    watch(currentLang, () => renderMarkers());

    const searchOnNaver = (query) => {
      const url = `https://search.naver.com/search.naver?query=${encodeURIComponent(query)}`;
      window.open(url, '_blank');
    };

    return { currentLang, t, hospitals, pharmacies, searchOnNaver, focusOnMap, mapContainer };
  }
}
</script>

<style scoped>
.apple-page { padding-bottom: 100px; }
.hero-section { padding: 80px 22px 40px; text-align: center; max-width: 800px; margin: 0 auto; }
.hero-title { font-size: 3.5rem; font-weight: 700; margin-bottom: 12px; }
.hero-subtitle { font-size: 1.5rem; color: var(--text-secondary); }
.map-section { max-width: 1024px; margin: 0 auto 80px; padding: 0 22px; }
.kakao-map { width: 100%; height: 500px; border-radius: 22px; overflow: hidden; display: block; }
.content-section { max-width: 1024px; margin: 0 auto; padding: 0 22px; }
.section-header { margin-bottom: 30px; }
.section-title { font-size: 2rem; font-weight: 600; }
.mt-80 { margin-top: 80px; }
.bento-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 20px; }
.apple-card { background: var(--card-bg); border-radius: 22px; padding: 30px; transition: transform 0.4s; min-height: 200px; border: 1px solid transparent; }
.apple-card:hover { transform: scale(1.02); border: 1px solid var(--accent); }
.card-tag { font-size: 0.75rem; font-weight: 700; margin-bottom: 12px; display: block; }
.card-tag.emergency { color: #ff3b30; }
.card-tag.pharmacy { color: #34c759; }
.address, .time, .info { font-size: 0.9rem; color: var(--text-secondary); margin-bottom: 4px; }
.clickable { cursor: pointer; }
.naver-btn {
  margin-top: 12px;
  background: #03c75a;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
}
.naver-btn:hover { background: #02b351; }
@media (max-width: 734px) {
  .hero-title { font-size: 2.5rem; }
  .bento-grid { grid-template-columns: 1fr; }
}
</style>
